import { useEffect, useState } from "react";
import { Volume2, RefreshCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { IDS } from "@/lib/testIds";
import { GAME_ITEMS, LETTERS, shuffle, randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS, GAME_SUCCESS_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function newRound() {
  const item = randomFrom(GAME_ITEMS);
  const wordLetters = item.word.split("");
  // Add 2 random distractor letters
  const distractors = shuffle(LETTERS.filter((l) => !wordLetters.includes(l))).slice(
    0,
    Math.max(2, 6 - wordLetters.length)
  );
  const pool = shuffle([...wordLetters, ...distractors]);
  return { item, pool };
}

export default function WordBuilding() {
  const [round, setRound] = useState(newRound);
  const [slots, setSlots] = useState([]); // array of {letter, poolIndex}
  const [usedPool, setUsedPool] = useState(new Set()); // pool indexes used
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState(null); // 'correct' | 'wrong' | null
  const { award } = useAuth();

  useEffect(() => {
    setSlots([]);
    setUsedPool(new Set());
    setResult(null);
    const t = setTimeout(() => {
      speak(`Build the word ${round.item.word}.`);
    }, 200);
    return () => clearTimeout(t);
  }, [round]);

  const targetLen = round.item.word.length;

  const pickLetter = (letter, i) => {
    if (result) return;
    if (usedPool.has(i)) return;
    if (slots.length >= targetLen) return;
    setSlots((s) => [...s, { letter, poolIndex: i }]);
    setUsedPool((u) => new Set(u).add(i));
  };

  const removeSlot = (slotIdx) => {
    if (result) return;
    const s = slots[slotIdx];
    if (!s) return;
    setSlots((cur) => cur.filter((_, idx) => idx !== slotIdx));
    setUsedPool((u) => {
      const next = new Set(u);
      next.delete(s.poolIndex);
      return next;
    });
  };

  const reset = () => {
    if (result) return;
    setSlots([]);
    setUsedPool(new Set());
  };

  const submit = () => {
    if (slots.length !== targetLen) {
      toast.error("Fill all letters first.");
      return;
    }
    setAttempts((a) => a + 1);
    const built = slots.map((s) => s.letter).join("");
    if (built === round.item.word) {
      setResult("correct");
      setScore((s) => s + 1);
      award(10, "build");
      toast.success(`Great! You built "${round.item.word}".`);
      speak(`${round.item.word}. Well done!`);
      setTimeout(() => setRound(newRound()), GAME_SUCCESS_MS);
    } else {
      setResult("wrong");
      toast.error(`Not quite — that spells "${built}".`);
      setTimeout(() => setResult(null), GAME_FEEDBACK_MS);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid={IDS.games.build.score}
          className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid={IDS.games.build.next}
          onClick={() => setRound(newRound())}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New word
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <div className="text-7xl mb-2">{round.item.emoji}</div>
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500">
          Build this word
        </p>
        <button
          onClick={() => speak(`${round.item.word}.`)}
          className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border-2 border-slate-200 hover:border-amber-400 text-slate-700 font-bold text-sm transition-colors"
        >
          <Volume2 className="w-4 h-4" /> Hear word
        </button>
      </div>

      {/* Slots */}
      <div
        className={`flex flex-wrap justify-center gap-3 mb-6 ${result === "wrong" ? "phonx-shake" : ""}`}
      >
        {Array.from({ length: targetLen }).map((_, i) => {
          const s = slots[i];
          const isFilled = !!s;
          const correctColor =
            result === "correct"
              ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop"
              : "bg-amber-50 border-amber-300 text-amber-700";
          return (
            <button
              key={`slot-${i}`}
              data-testid={IDS.games.build.slot(i)}
              onClick={() => removeSlot(i)}
              className={`w-16 h-20 sm:w-20 sm:h-24 rounded-2xl border-2 font-display text-4xl sm:text-5xl font-semibold flex items-center justify-center transition-all
                ${isFilled ? correctColor : "bg-white border-dashed border-slate-300 text-slate-300"}
              `}
            >
              {s?.letter || "_"}
            </button>
          );
        })}
      </div>

      {/* Pool */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6">
        {round.pool.map((letter, i) => {
          const used = usedPool.has(i);
          return (
            <button
              key={`pool-${letter}-${i}`}
              data-testid={IDS.games.build.letter(i)}
              onClick={() => pickLetter(letter, i)}
              disabled={used}
              className={`w-12 h-14 sm:w-14 sm:h-16 rounded-2xl border-2 font-display text-2xl sm:text-3xl font-semibold transition-all
                ${used
                  ? "bg-slate-100 border-slate-100 text-slate-300"
                  : "bg-white border-slate-200 text-slate-800 hover:border-amber-400 hover:text-amber-600 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                }
              `}
            >
              {letter}
            </button>
          );
        })}
      </div>

      <div className="flex justify-center gap-3">
        <button
          data-testid={IDS.games.build.reset}
          onClick={reset}
          className="rounded-full px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold hover:border-slate-400 transition-colors"
        >
          Clear
        </button>
        <button
          data-testid={IDS.games.build.submit}
          onClick={submit}
          className="rounded-full px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
        >
          <Check className="w-5 h-5" /> Check
        </button>
      </div>
    </div>
  );
}
